import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Button,
  Autocomplete,
  Divider,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  MenuBook as BookIcon,
} from "@mui/icons-material";
import { usePermissions } from "../../hooks";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useNavigate } from "react-router";
import { useTranslate } from "@refinedev/core";
import { Property } from "../../interfaces/property";
import { PropertyStatusChip } from "../../components/property/status";

// API utilities
import { apiVersion, apiJson } from "../../utils/api";
import ServerPagination from "../../components/pagination/ServerPagination";
import * as Sentry from "@sentry/react";
// Map snippet removed

// Property interface is imported from interfaces/property.ts
// Languages are now dynamically extracted from property.content keys

type ProfileSearchItem = {
  id: string;
  name: string;
  photo?: { url?: string };
};

type BatchPublishError = {
  uid: string;
  error: string;
};

type BatchPublishResponse = {
  url: string;
  invoice_pdf: string;
  properties: Array<{ uid: string; name: string; status: string }>;
  errors: Array<{ uid: string; error: string }>;
};

// (Pagination is handled via the generic hook below)

const PropertyProfileInline: React.FC<{ profile: any }> = ({ profile }) => (
  <Box sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
    <Divider sx={{ my: 0.5 }} />
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        backgroundColor: 'action.hover',
        borderRadius: 1,
        width: '100%',
        flexShrink: 0
      }}
    >
      <Avatar src={profile?.photo?.url} sx={{ width: 40, height: 40 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" display="block" color="text.secondary" fontSize="0.75rem">
          {profile?.id}
        </Typography>
        <Typography variant="body2" fontWeight="bold" fontSize="0.875rem">
          {profile?.name}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export const PropertiesList: React.FC = () => {
  const { hasFullAccess } = usePermissions();
  const t = useTranslate();
  const [searchText, setSearchText] = useState("");
  const [selectedProfile, setSelectedProfile] = useState<ProfileSearchItem | null>(null);
  const [profileSearchText, setProfileSearchText] = useState("");
  const [profileOptions, setProfileOptions] = useState<ProfileSearchItem[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Multi-select state
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  
  // Bulk invoicing dialog states
  const [bulkInvoiceDialogOpen, setBulkInvoiceDialogOpen] = useState(false);
  const [bulkDiscountDialogOpen, setBulkDiscountDialogOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);
  const [bulkInvoiceLoading, setBulkInvoiceLoading] = useState(false);
  const [bulkInvoiceError, setBulkInvoiceError] = useState<string | null>(null);
  const [bulkInvoiceResult, setBulkInvoiceResult] = useState<BatchPublishResponse | null>(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  
  // Pagination handled by generic hook
  const [photoIndexes, setPhotoIndexes] = useState<{ [key: string]: number }>({});
  const navigate = useNavigate();

  const handlePhotoNavigation = (propertyId: string, direction: 'prev' | 'next', totalPhotos: number) => {
    const currentIndex = photoIndexes[propertyId] || 0;
    if (!totalPhotos || totalPhotos <= 0) return;
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? totalPhotos - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === totalPhotos - 1 ? 0 : currentIndex + 1;
    }
    
    setPhotoIndexes(prev => ({ ...prev, [propertyId]: newIndex }));
  };

  // Profile search handler
  const handleProfileSearch = useCallback(async (searchValue: string) => {
    // Ensure searchValue is a string before processing - guard against undefined from MUI Autocomplete
    const trimmedValue = typeof searchValue === 'string' ? searchValue.trim() : '';
    if (!trimmedValue || trimmedValue.length < 3) {
      setProfileOptions([]);
      return;
    }

    setProfileLoading(true);
    try {
      const params = new URLSearchParams({
        search: trimmedValue,
        search_field: "name",
        page: "1",
        limit: "10",
      });
      const response = await apiJson<{
        data: ProfileSearchItem[];
        pagination: { total: number };
      }>(`admin/${apiVersion}/profiles?${params.toString()}`);
      setProfileOptions(response.data || []);
    } catch (error) {
      console.error("Failed to search profiles:", error);
      setProfileOptions([]);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Load initial profiles on mount
  const loadInitialProfiles = useCallback(async () => {
    setProfileLoading(true);
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "10",
        order_field: "id",
        order: "asc",
      });
      const response = await apiJson<{
        data: ProfileSearchItem[];
        pagination: { total: number };
      }>(`admin/${apiVersion}/profiles?${params.toString()}`);
      setProfileOptions(response.data || []);
    } catch (error) {
      console.error("Failed to load profiles:", error);
      setProfileOptions([]);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialProfiles();
  }, [loadInitialProfiles]);

  // Clear selection when profile changes
  const handleProfileChange = (newProfile: ProfileSearchItem | null) => {
    setSelectedProfile(newProfile);
    setSelectedProperties(new Set());
  };

  // Toggle property selection
  const togglePropertySelection = (propertyUid: string) => {
    const newSelection = new Set(selectedProperties);
    if (newSelection.has(propertyUid)) {
      newSelection.delete(propertyUid);
    } else {
      newSelection.add(propertyUid);
    }
    setSelectedProperties(newSelection);
  };

  // Bulk invoice handler
  const handleBulkInvoice = async (invoiceAll: boolean = false) => {
    if (!invoiceAll && selectedProperties.size === 0) return;

    setBulkInvoiceLoading(true);
    setBulkInvoiceError(null);

    try {
      const body: any = {
        discount: selectedMonths !== null && selectedMonths > 0,
        period: selectedMonths ?? 3
      };

      if (!invoiceAll) {
        body.property_ids = Array.from(selectedProperties);
      } else {
        body.profile_id = selectedProfile?.id;
      }

      const response = await apiJson<BatchPublishResponse>(
        `admin/${apiVersion}/properties/batch/publish`,
        {
          method: "POST",
          body: JSON.stringify(body),
        }
      );

      setBulkInvoiceResult(response);
      setResultsModalOpen(true);
      setBulkDiscountDialogOpen(false);
      
      // Clear selection after successful invoice
      setSelectedProperties(new Set());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to publish guidebooks";
      setBulkInvoiceError(errorMessage);
      console.error("Batch invoice error:", error);
      
      Sentry.captureException(error, {
        tags: {
          priority: "critical",
          context: "bulk_invoice",
        },
        extra: {
          properties: invoiceAll ? "all" : Array.from(selectedProperties),
          months: selectedMonths,
        },
      });
    } finally {
      setBulkInvoiceLoading(false);
    }
  };

  // Function to get available languages for a property
  // Uses property.content to get the actual available languages
  const getPropertyLanguages = (property: Property): string[] => {
    try {
      // Guard against malformed content structures that could be rendered as React children
      if (!property.content || typeof property.content !== 'object' || Array.isArray(property.content)) {
        console.warn('Invalid content structure for property:', property.uid);
        return [];
      }

      // If content looks like a PropertyDetailsContent (has header/content keys), skip to avoid rendering objects
      if ('header' in property.content && 'content' in property.content) {
        console.warn('Content appears to be PropertyDetailsContent instead of language map:', property.uid);
        return [];
      }

      return Object.keys(property.content || {});
    } catch (error) {
      console.warn('Error getting languages for property:', property.uid, error);
      return [];
    }
  };

  const handleCreateProperty = () => {
    navigate('/properties/new');
  };

  const getDescriptionText = (property: Property): string => {
    try {
      // Get description from the default language content
      const contentMap = property.content;

      // Guard against malformed content structures
      if (!contentMap || typeof contentMap !== 'object' || Array.isArray(contentMap)) {
        console.warn('Invalid content structure (description):', property.uid);
        return "";
      }
      if ('header' in contentMap && 'content' in contentMap) {
        console.warn('Content appears to be PropertyDetailsContent (description):', property.uid);
        return "";
      }

      const defaultContent = contentMap?.[property.default_language];
      if (!defaultContent?.description) return "";
      
      const description = defaultContent.description;
      
      // Limit to 120 characters and add ellipsis if longer
      if (description.length > 120) {
        return description.substring(0, 120) + "...";
      }
      
      return description;
    } catch (error) {
      console.warn('Error getting description for property:', property.uid, error);
      return "";
    }
  };

  const getLocationText = (property: Property): string => {
    try {
      // Get location from the default language content, fallback to address
      const contentMap = property.content;

      // Guard against malformed content structures
      if (!contentMap || typeof contentMap !== 'object' || Array.isArray(contentMap)) {
        console.warn('Invalid content structure (location):', property.uid);
        return property.address || "";
      }
      if ('header' in contentMap && 'content' in contentMap) {
        console.warn('Content appears to be PropertyDetailsContent (location):', property.uid);
        return property.address || "";
      }

      const defaultContent = contentMap?.[property.default_language];
      return defaultContent?.location || property.address || "";
    } catch (error) {
      console.warn('Error getting location for property:', property.uid, error);
      return property.address || "";
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      padding: 2
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          Properties
        </Typography>
      </Box>
      
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", flex: 1 }}>
          <TextField
            placeholder="Search by property name, location, or profile ID"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />

          {hasFullAccess && (
            <Autocomplete
              options={profileOptions}
              getOptionLabel={(option) => `${option.name} (${option.id})`}
              value={selectedProfile}
              onChange={(e, newValue) => handleProfileChange(newValue)}
              inputValue={profileSearchText}
              onInputChange={(e, newInputValue) => {
                setProfileSearchText(newInputValue);
                handleProfileSearch(newInputValue);
              }}
              loading={profileLoading}
              sx={{ width: { xs: '100%', sm: 300 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("properties.filters.profile")}
                  placeholder="Search profile..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    endAdornment: (
                      <>
                        {profileLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {option.photo?.url && (
                    <Avatar src={option.photo.url} sx={{ width: 32, height: 32 }} />
                  )}
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.id}</Typography>
                  </Box>
                </li>
              )}
            />
          )}
        </Box>

        {!hasFullAccess && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/properties/new")}
              startIcon={<AddIcon />}
              sx={{ whiteSpace: "nowrap", height: "56px" }}
            >
              Create property
            </Button>
          </Box>
        )}
      </Box>

      <ServerPagination<Property>
        endpoint={`admin/${apiVersion}/properties`}
        search={searchText}
        extraParams={selectedProfile ? { profile_id: selectedProfile.id } : {}}
        initialLimit={10}
        pageSizeOptions={[5, 10, 15, 20]}
      >
        {(items) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((property) => {
            const currentPhotoIndex = photoIndexes[property.uid] || 0;
            const currentPhoto = property.photos?.[currentPhotoIndex];
            const totalPhotos = property.photos?.length || 0;
            const hasAdminAccess = hasFullAccess || property.is_admin === true;
            const showProfileCard = hasAdminAccess && property.profile;
            const isSelected = selectedProperties.has(property.uid);
            
            return (
              <Card 
                key={property.uid} 
                sx={{ 
                  width: '100%', 
                  borderRadius: 2, 
                  backgroundColor: 'background.paper', 
                  overflow: 'hidden',
                  cursor: hasFullAccess ? 'pointer' : 'default',
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': hasFullAccess ? { borderColor: 'primary.main', boxShadow: 2 } : {},
                }} 
                onClick={() => !hasFullAccess && navigate(`/properties/${property.uid}`)}
              >
                <CardContent sx={{ p: 3, '&:last-child': { paddingBottom: 3 } }}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {/* Checkbox - only for FOUNDER/is_admin and when profile is selected */}
                    {hasFullAccess && selectedProfile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 40 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => togglePropertySelection(property.uid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                    )}

                    {/* Photo Gallery Section (left on desktop, top on mobile) */}
                    <Box sx={{ position: 'relative', width: { xs: '100%', sm: 200 }, height: { xs: 220, sm: 200 }, flexShrink: 0 }}>
                      <Avatar
                        src={currentPhoto?.url}
                        variant="rounded"
                        sx={{ 
                          width: '100%', 
                          height: '100%',
                          borderRadius: 2,
                          objectFit: 'cover'
                        }}
                      />
                      
                      {/* Photo Navigation */}
                      {totalPhotos > 1 && (
                        <>
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handlePhotoNavigation(property.uid, 'prev', totalPhotos); }}
                            sx={{
                              position: 'absolute',
                              left: 6,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                              width: 28,
                              height: 28
                            }}
                          >
                            <PrevIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handlePhotoNavigation(property.uid, 'next', totalPhotos); }}
                            sx={{
                              position: 'absolute',
                              right: 6,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                              width: 28,
                              height: 28
                            }}
                          >
                            <NextIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      
                      {/* Photo Counter */}
                      {totalPhotos > 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          {currentPhotoIndex + 1}/{totalPhotos}
                        </Box>
                      )}
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: { xs: 'auto', sm: 200 }, justifyContent: 'space-between' }}>
                      {/* Top Content */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {/* Name + Status */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            sx={{ lineHeight: 1.2, cursor: 'pointer' }}
                            onClick={() => navigate(`/properties/${property.uid}`)}
                          >
                            {property.name}
                          </Typography>
                          {property.status && (
                            <PropertyStatusChip value={property.status} />
                          )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                          {getLocationText(property)}
                        </Typography>

                        {/* Description */}
                        <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                          {getDescriptionText(property)}
                        </Typography>
                      </Box>

                      {/* Bottom Left - Languages */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <Typography variant="body2" fontWeight="medium" fontSize="0.875rem">
                          {t('properties.fields.languages.label')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {getPropertyLanguages(property).map((lang) => (
                            <Chip
                              key={lang}
                              label={t(`properties.fields.languages.${lang}`)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {showProfileCard && <PropertyProfileInline profile={property.profile} />}
                </CardContent>
              </Card>
            );
          })}
          </Box>
        )}
      </ServerPagination>

      {/* Bulk Discount Dialog */}
      <Dialog 
        open={bulkDiscountDialogOpen} 
        onClose={() => !bulkInvoiceLoading && setBulkDiscountDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("properties.bulkInvoice.dialogs.discount.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("properties.bulkInvoice.dialogs.discount.description")}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {t("properties.bulkInvoice.dialogs.discount.selectMonths")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
              (Optional - click a selected button to deselect)
            </Typography>
            <ToggleButtonGroup
              value={selectedMonths}
              exclusive
              onChange={(e, value) => setSelectedMonths(value)}
              fullWidth
            >
              <ToggleButton value={3}>3 months</ToggleButton>
              <ToggleButton value={6}>6 months</ToggleButton>
              <ToggleButton value={12}>12 months</ToggleButton>
            </ToggleButtonGroup>
            {bulkInvoiceError && (
              <Alert severity="error">{bulkInvoiceError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDiscountDialogOpen(false)} 
            disabled={bulkInvoiceLoading}
          >
            {t("common.actions.cancel")}
          </Button>
          <Button 
            onClick={() => handleBulkInvoice(selectedProperties.size === 0)}
            variant="contained" 
            disabled={bulkInvoiceLoading}
          >
            {bulkInvoiceLoading ? <CircularProgress size={20} /> : t("properties.bulkInvoice.dialogs.discount.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Modal */}
      <Dialog
        open={resultsModalOpen}
        onClose={() => setResultsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("properties.bulkInvoice.dialogs.results.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Summary Section */}
            {bulkInvoiceResult && bulkInvoiceResult.properties.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="body1">
                  {bulkInvoiceResult.properties.length} guidebook{bulkInvoiceResult.properties.length > 1 ? 's' : ''} published successfully
                </Typography>
              </Box>
            )}

            {/* Invoice Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {bulkInvoiceResult?.url && (
                <Button size="small" variant="contained" href={bulkInvoiceResult.url} target="_blank">
                  View Invoice URL
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => bulkInvoiceResult?.url && navigator.clipboard.writeText(bulkInvoiceResult.url)}
              >
                Copy URL
              </Button>
              {bulkInvoiceResult?.invoice_pdf && (
                <Button size="small" variant="outlined" href={bulkInvoiceResult.invoice_pdf} target="_blank">
                  Download PDF
                </Button>
              )}
            </Box>

            {/* Property List - Collapsible */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Show guidebooks ({bulkInvoiceResult?.properties.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {bulkInvoiceResult?.properties.map((prop) => (
                    <Box key={prop.uid} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <BookIcon fontSize="small" />
                      <Typography sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {prop.name.length > 20 ? prop.name.substring(0, 20) + '...' : prop.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{prop.uid}</Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Errors Section */}
            {bulkInvoiceResult && bulkInvoiceResult.errors && bulkInvoiceResult.errors.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="error" />
                  <Typography variant="h6">Errors</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>Property UID</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkInvoiceResult.errors.map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontSize: '0.875rem' }}>{err.uid}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>{err.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsModalOpen(false)} variant="contained">
            {t("common.actions.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
